// Client/src/pages/digi/Invoice/Components/templateApi.js
//
// Vendor invoice templates + property (billing) groups. One module for every
// screen that touches them: the vendor's Property Groups and Invoice Templates
// tabs, the template designer, the staff review queue, and the invoice form.
// Server: Server/src/controllers/invoice/invoice-template/*, invoice.billing-group.*

import axios from "../../../api/axios";
import { toast } from "react-toastify";

export const TEMPLATE_ENDPOINTS = {
  GROUPS: "/v2/invoices/billing-groups",
  GROUP: "/v2/invoices/billing-groups/:id",
  GROUP_ARCHIVE: "/v2/invoices/billing-groups/:id/archive",
  GROUP_RESTORE: "/v2/invoices/billing-groups/:id/restore",

  TEMPLATES: "/v2/invoices/templates",
  STARTERS: "/v2/invoices/templates/starters",
  FIELDS: "/v2/invoices/templates/fields",
  SAMPLE_INVOICES: "/v2/invoices/templates/sample-invoices",
  COMPANIES: "/v2/invoices/templates/companies",
  EFFECTIVE: "/v2/invoices/templates/effective",
  EFFECTIVE_PREVIEW: "/v2/invoices/templates/effective/preview-pdf",
  TEMPLATE: "/v2/invoices/templates/:id",
  DRAFT: "/v2/invoices/templates/:id/draft",
  RENDER: "/v2/invoices/templates/:id/render",
  PREVIEW_PDF: "/v2/invoices/templates/:id/preview-pdf",
  DUPLICATE: "/v2/invoices/templates/:id/duplicate",
  DEFAULT: "/v2/invoices/templates/:id/default",
  ARCHIVE: "/v2/invoices/templates/:id/archive",
  SUBMIT: "/v2/invoices/templates/:id/submit",
  ASSET_REQUEST: "/v2/invoices/templates/:id/assets/request-upload",
  ASSET_CONFIRM: "/v2/invoices/templates/:id/assets/confirm",
  ASSET_REMOVE: "/v2/invoices/templates/:id/assets/:assetId/remove",

  // Staff review queue (company reviewers).
  REVIEW_LIST: "/v2/invoices/templates/review",
  REVIEW_ONE: "/v2/invoices/templates/review/:id",
  REVIEW_RENDER: "/v2/invoices/templates/review/:id/render",
  REVIEW_DECIDE: "/v2/invoices/templates/review/:id/decision",
};

const url = (path, params = {}) =>
  Object.entries(params).reduce((p, [k, v]) => p.replace(`:${k}`, encodeURIComponent(v)), path);

// `quiet` callers handle the error themselves (e.g. autosave conflicts).
async function call(fn, failMessage, { quiet = false } = {}) {
  try {
    const res = await fn();
    return res.data?.data !== undefined ? res.data.data : res.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message || failMessage;
    if (!quiet) toast.error(message);
    const err = new Error(message);
    err.status = error.response?.status;
    throw err;
  }
}

// ── Property (billing) groups ────────────────────────────────────────────
// group: { _id, name, companyId, buildings: [{ propertyId, buildingName }], billTo, templateId, isActive }
export const listBillingGroups = (params = {}) =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.GROUPS, { params }), "Could not load property groups");
export const createBillingGroup = (payload) =>
  call(() => axios.post(TEMPLATE_ENDPOINTS.GROUPS, payload), "Could not create the property group");
export const updateBillingGroup = (id, payload) =>
  call(() => axios.put(url(TEMPLATE_ENDPOINTS.GROUP, { id }), payload), "Could not save the property group");
export const archiveBillingGroup = (id) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.GROUP_ARCHIVE, { id })), "Could not archive the property group");
export const restoreBillingGroup = (id) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.GROUP_RESTORE, { id })), "Could not restore the property group");

// ── Templates (vendor) ───────────────────────────────────────────────────
// list row: { _id, name, isDefault, isArchived, numbering:{prefix,nextNo,pad}, updatedAt, versionCount,
//             hasUnsubmittedChanges, approvals:[{ companyId, companyName, version, status, approvedVersion, note, decidedAt }] }
export const listTemplates = (params = {}) =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.TEMPLATES, { params }), "Could not load templates");
export const getStarters = () => call(() => axios.get(TEMPLATE_ENDPOINTS.STARTERS), "Could not load starters");
// { groups: [{ group, fields: [{ key, label, kind }] }], dateFormats, moneyFormats, numberFormats }
export const getTemplateFields = () => call(() => axios.get(TEMPLATE_ENDPOINTS.FIELDS), "Could not load fields");
// [{ groupId, label, total }]
export const getSampleInvoices = () =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.SAMPLE_INVOICES), "Could not load your invoices", { quiet: true });
// Companies the vendor may submit a template to: [{ _id, name }]
export const getTemplateCompanies = () =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.COMPANIES), "Could not load companies");
export const createTemplate = (payload) =>
  call(() => axios.post(TEMPLATE_ENDPOINTS.TEMPLATES, payload), "Could not create the template");
// { ...template, draft, draftRev, assets, versions, approvals, numbering, issues }
export const getTemplate = (id) => call(() => axios.get(url(TEMPLATE_ENDPOINTS.TEMPLATE, { id })), "Could not load the template");
export const updateTemplate = (id, payload) =>
  call(() => axios.patch(url(TEMPLATE_ENDPOINTS.TEMPLATE, { id }), payload), "Could not update the template");
// → { rev, issues, design }; 409 when another tab saved first (err.status === 409)
export const saveTemplateDraft = (id, design, rev) =>
  call(() => axios.put(url(TEMPLATE_ENDPOINTS.DRAFT, { id }), { design, rev }), "Could not save", { quiet: true });
// sample: 'demo' | invoice groupId → { html }
export const renderTemplateHtml = (id, design, sample = "demo") =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.RENDER, { id }), { design, sample }), "Could not render", { quiet: true });
export const previewTemplatePdf = async (id, design, sample = "demo") => {
  const blob = await call(
    () => axios.post(url(TEMPLATE_ENDPOINTS.PREVIEW_PDF, { id }), { design, sample }, { responseType: "blob" }),
    "Could not build the PDF preview"
  );
  return URL.createObjectURL(blob);
};
export const duplicateTemplate = (id) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.DUPLICATE, { id })), "Could not duplicate the template");
export const makeTemplateDefault = (id) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.DEFAULT, { id })), "Could not set the default template");
export const archiveTemplate = (id, restore = false) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.ARCHIVE, { id }), { restore }), "Could not archive the template");
// Freezes the saved draft as the next version and asks these companies to approve it.
// companyIds omitted = every company the vendor invoices. → updated list row
export const submitTemplate = (id, companyIds) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.SUBMIT, { id }), { companyIds }), "Could not submit the template");

// Uploads an image to the template; resolves to the template's asset list.
export const uploadTemplateAsset = async (id, file) => {
  const dims = await imageSize(file).catch(() => ({}));
  const { assetId, key, signedUrl } = await call(
    () => axios.post(url(TEMPLATE_ENDPOINTS.ASSET_REQUEST, { id }), { fileName: file.name, contentType: file.type, size: file.size }),
    "Could not start the upload"
  );
  const put = await fetch(signedUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
  if (!put.ok) {
    toast.error("The image upload failed — try again");
    throw new Error("upload failed");
  }
  return call(
    () => axios.post(url(TEMPLATE_ENDPOINTS.ASSET_CONFIRM, { id }), { assetId, key, filename: file.name, ...dims }),
    "Could not save the image"
  );
};
export const removeTemplateAsset = (id, assetId) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.ASSET_REMOVE, { id, assetId })), "Could not remove the image");

function imageSize(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(src);
    };
    img.onerror = reject;
    img.src = src;
  });
}

// ── Invoice form ─────────────────────────────────────────────────────────
// The template that will generate this invoice, if any.
// params: { companyId, billingGroupId?, vendorId? (staff: InvoiceVendor catalog id) }
// → { template: { _id, name, version } | null, nextNumber: "GSK 271" | null }
export const getEffectiveTemplate = (params) =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.EFFECTIVE, { params }), "Could not check your invoice template", { quiet: true });
// The PDF the server would generate for the form as it stands (same payload as createInvoice
// plus companyId / billingGroupId / vendorId). Resolves to an object URL.
export const previewEffectivePdf = async (payload) => {
  const blob = await call(async () => {
    try {
      return await axios.post(TEMPLATE_ENDPOINTS.EFFECTIVE_PREVIEW, payload, { responseType: "blob" });
    } catch (error) {
      // A blob response hides the server's JSON error; read it so the toast
      // says why (e.g. a missing unit number) instead of "status code 400".
      const data = error.response?.data;
      if (data instanceof Blob) {
        try { error.response.data = JSON.parse(await data.text()); } catch { /* not JSON */ }
      }
      throw error;
    }
  }, "Could not build the invoice preview");
  return URL.createObjectURL(blob);
};

// ── Staff review ─────────────────────────────────────────────────────────
// [{ templateId, name, vendor: { _id, name, email }, companyId, companyName, version, status,
//    approvedVersion, note, submittedAt, decidedAt }]
export const listTemplateReviews = (params = {}) =>
  call(() => axios.get(TEMPLATE_ENDPOINTS.REVIEW_LIST, { params }), "Could not load vendor templates");
// { templateId, name, vendor, companyId, version, status, approvedVersion, note, issues, versions: [{ version, submittedAt }] }
export const getTemplateReview = (id, companyId) =>
  call(() => axios.get(url(TEMPLATE_ENDPOINTS.REVIEW_ONE, { id }), { params: { companyId } }), "Could not load the template");
// version: number; sample: 'demo' | 'latest' → { html }
export const renderTemplateReview = (id, companyId, version, sample = "demo") =>
  call(
    () => axios.post(url(TEMPLATE_ENDPOINTS.REVIEW_RENDER, { id }), { companyId, version, sample }),
    "Could not render the template",
    { quiet: true }
  );
// decision: 'approve' | 'changes_requested' (note required for changes)
export const decideTemplateReview = (id, payload) =>
  call(() => axios.post(url(TEMPLATE_ENDPOINTS.REVIEW_DECIDE, { id }), payload), "Could not save the decision");

export const APPROVAL_LABEL = {
  pending: { text: "Pending review", tone: "warning" },
  approved: { text: "Approved", tone: "success" },
  changes_requested: { text: "Changes requested", tone: "danger" },
};
